import * as React from 'react';
import { cn } from './cn';

interface BaseFieldProps {
	label?: string;
	hint?: string;
	error?: string;
	required?: boolean;
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
	containerClassName?: string;
}

export interface InputProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
		BaseFieldProps {
	inputSize?: 'sm' | 'md' | 'lg';
}

const sizes = {
	sm: 'h-9 text-sm px-3',
	md: 'h-11 text-sm px-3.5',
	lg: 'h-12 text-base px-4',
};

/**
 * Plain `<input>` wrapper with label/error scaffolding. We intentionally use
 * React.forwardRef so react-hook-form's register() ref attaches correctly —
 * a wrapper without forwardRef is the silent "all fields invalid" bug.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
	(
		{
			label,
			hint,
			error,
			required,
			leftIcon,
			rightIcon,
			inputSize = 'md',
			containerClassName,
			className,
			id,
			...rest
		},
		ref
	) => {
		const reactId = React.useId();
		const inputId = id || `input-${reactId}`;
		const describedBy = error
			? `${inputId}-error`
			: hint
			? `${inputId}-hint`
			: undefined;

		return (
			<div className={cn('w-full', containerClassName)}>
				{label && (
					<label
						htmlFor={inputId}
						className={cn('field-label mb-1.5', required && 'field-required')}
					>
						{label}
					</label>
				)}
				<div className="relative">
					{leftIcon && (
						<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-ink-muted pointer-events-none">
							{leftIcon}
						</span>
					)}
					<input
						ref={ref}
						id={inputId}
						aria-invalid={!!error}
						aria-describedby={describedBy}
						className={cn(
							'block w-full rounded-lg border bg-white text-ink placeholder:text-ink-faint',
							'transition-colors',
							sizes[inputSize],
							leftIcon && 'pl-10',
							rightIcon && 'pr-10',
							error
								? 'border-danger-400 focus:border-danger-500 focus:ring-2 focus:ring-danger-200'
								: 'border-slate-300 focus:border-brand-600 focus:ring-2 focus:ring-brand-100',
							'disabled:bg-slate-50 disabled:text-ink-muted disabled:cursor-not-allowed',
							className
						)}
						{...rest}
					/>
					{rightIcon && (
						<span className="absolute inset-y-0 right-0 flex items-center pr-3 text-ink-muted">
							{rightIcon}
						</span>
					)}
				</div>
				{error ? (
					<p id={`${inputId}-error`} className="field-error">
						{error}
					</p>
				) : hint ? (
					<p id={`${inputId}-hint`} className="field-hint">
						{hint}
					</p>
				) : null}
			</div>
		);
	}
);
Input.displayName = 'Input';

export default Input;

// --- Textarea --------------------------------------------------------------

export interface TextareaProps
	extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
		BaseFieldProps {}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
	(
		{
			label,
			hint,
			error,
			required,
			containerClassName,
			className,
			id,
			rows = 3,
			...rest
		},
		ref
	) => {
		const reactId = React.useId();
		const inputId = id || `textarea-${reactId}`;
		return (
			<div className={cn('w-full', containerClassName)}>
				{label && (
					<label
						htmlFor={inputId}
						className={cn('field-label mb-1.5', required && 'field-required')}
					>
						{label}
					</label>
				)}
				<textarea
					ref={ref}
					id={inputId}
					rows={rows}
					aria-invalid={!!error}
					className={cn(
						'block w-full rounded-lg border bg-white text-ink placeholder:text-ink-faint',
						'px-3.5 py-2.5 text-sm transition-colors resize-y',
						error
							? 'border-danger-400 focus:border-danger-500 focus:ring-2 focus:ring-danger-200'
							: 'border-slate-300 focus:border-brand-600 focus:ring-2 focus:ring-brand-100',
						'disabled:bg-slate-50 disabled:text-ink-muted disabled:cursor-not-allowed',
						className
					)}
					{...rest}
				/>
				{error ? (
					<p className="field-error">{error}</p>
				) : hint ? (
					<p className="field-hint">{hint}</p>
				) : null}
			</div>
		);
	}
);
Textarea.displayName = 'Textarea';
